/**
 * Coordinate conversion utilities for circular layouts
 * Handles conversion between world coordinates (-1 to 1) and screen coordinates (pixels)
 */

import type { XYCoord } from '@/lib/colorConversion'

export interface CircleContext {
  centerX: number  // Screen X coordinate of circle center
  centerY: number  // Screen Y coordinate of circle center
  radius: number   // Circle radius in pixels
}

/**
 * Convert world coordinates (-1 to 1) to screen coordinates (pixels)
 * @param position World coordinates normalized to circle
 * @param context Circle dimensions and position
 * @returns Screen coordinates in pixels
 */
export function worldToScreen(
  position: XYCoord,
  context: CircleContext
): { x: number; y: number } {
  return {
    x: context.centerX + position.x * context.radius,
    y: context.centerY + position.y * context.radius
  }
}

/**
 * Convert screen coordinates (pixels) to world coordinates (-1 to 1)
 * @param screenX Screen X coordinate in pixels
 * @param screenY Screen Y coordinate in pixels
 * @param context Circle dimensions and position
 * @returns World coordinates normalized to circle
 */
export function screenToWorld(
  screenX: number,
  screenY: number,
  context: CircleContext
): XYCoord {
  return {
    x: (screenX - context.centerX) / context.radius,
    y: (screenY - context.centerY) / context.radius
  }
}

/**
 * Get circle context from an HTML element
 * Assumes the element is the circle container
 * @param element Circle container element
 * @returns Circle context with center and radius
 */
export function getCircleContext(element: HTMLElement): CircleContext {
  const rect = element.getBoundingClientRect()
  return {
    centerX: rect.left + rect.width / 2,
    centerY: rect.top + rect.height / 2,
    radius: rect.width / 2
  }
}

/**
 * Calculate pip style for positioning
 * @param position World coordinates
 * @param context Circle context
 * @param size Pip size in pixels
 * @returns Style object for absolute positioning
 */
export function getPipStyle(
  position: XYCoord,
  context: CircleContext,
  size: number = 24
): {
  left: string
  top: string
  width: string
  height: string
  transform: string
} {
  const screen = worldToScreen(position, context)
  
  return {
    left: `${screen.x}px`,
    top: `${screen.y}px`,
    width: `${size}px`,
    height: `${size}px`,
    transform: 'translate(-50%, -50%)'
  }
}

/**
 * Calculate distance between two points
 * @param p1 First point
 * @param p2 Second point
 * @returns Distance in same units as input
 */
export function distance(
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Check if a point is within a circle
 * @param point Point to check (world coordinates)
 * @param radius Circle radius (in world coordinates, typically 1.0)
 * @returns True if point is inside circle
 */
export function isInCircle(point: XYCoord, radius: number = 1.0): boolean {
  return distance({ x: 0, y: 0 }, point) <= radius
}
